import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Button from "@cayuse/shared-react.components.button";
import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import ConfirmOverwriteModal from "components/BudgetBuilder/Setup/ConfirmOverwriteModal";
import CopyToFrom from "components/BudgetBuilder/Setup/CopyToFrom";
import CurrencyFormatSelector from "components/BudgetBuilder/Setup/CurrencyFormatSelector";
import FARateSetup from "components/BudgetBuilder/Setup/FARateSetup";
import { DEFAULT_BUDGET_PERIODS } from "globals/constants";
import { useDictionary } from "hooks";
import { saveSetupData } from "store/actions/budgetBuilder";
import { budgetBuilderSelectors } from "store/selectors";
import { willCopyToFromOverwriteData } from "utils/budgetBuilder";

import RateTypeDropdown from "../RateTypeDropdown";
import UnitFinder from "../UnitFinder";
import styles from "./styles.css";

const SetupMainView = () => {
	const dictionary = useDictionary("budgetBuilder.buttonLabels");
	const [ setupData, setSetupData ] = useState({});
	const [ editable, setEditable ] = useState(false);
	const [ confirmOverwriteDataOpen, setConfirmOverwriteDataOpen ] = useState(false);
	const budgetBuilderFormContent = useSelector(budgetBuilderSelectors.budgetBuilderFormContent);

	useEffect(() => {
		if (!isNonEmptyObject(setupData) && isNonEmptyObject(budgetBuilderFormContent)) {
			setSetupData({
				budgetPeriods: budgetBuilderFormContent.budgetPeriods,
				rateType: budgetBuilderFormContent.rateType,
				unit: budgetBuilderFormContent.unit,
				displayCents: budgetBuilderFormContent.displayCents,
				copyToFrom: [ {} ]
			});
		}
	}, [ budgetBuilderFormContent, setupData ]);

	const onUnitChange = unit => setSetupData({ ...setupData, unit });
	const onRateTypeChange = rateType => setSetupData({ ...setupData, rateType });
	const onBudgetPeriodsChange = budgetPeriods => setSetupData({ ...setupData, budgetPeriods });
	const onDisplayCentsChange = displayCents => setSetupData({ ...setupData, displayCents });
	const onCopyToFromChange = copyToFrom => setSetupData({ ...setupData, copyToFrom });

	const onEdit = () => setEditable(true);

	const doSave = () => saveSetupData(setupData).then(() => {
		setSetupData({
			...setupData,
			copyToFrom: [ {} ],
		});
		setEditable(false);
	});

	const onConfirmOverwrite = () => {
		setConfirmOverwriteDataOpen(false);
		doSave();
	};

	const onSave = () => {
		if (willCopyToFromOverwriteData(setupData?.copyToFrom, budgetBuilderFormContent)) {
			setConfirmOverwriteDataOpen(true);
		} else {
			doSave();
		}
	};
	const onCancel = () => {
		setEditable(false);
		setSetupData({
			budgetPeriods: budgetBuilderFormContent.budgetPeriods,
			rateType: budgetBuilderFormContent.rateType,
			unit: budgetBuilderFormContent.unit,
			copyToFrom: [ {} ],
		});
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.topContainer}>
					<UnitFinder
						editable={editable}
						className={styles.setupDropdown}
						unit={setupData.unit}
						onChange={onUnitChange}
					/>
					<RateTypeDropdown
						editable={editable}
						className={styles.setupDropdown}
						unitId={setupData.unit}
						onChange={onRateTypeChange}
						rateType={setupData.rateType}
					/>
				</div>
				<div>
					<Button secondary disabled={!editable} onClick={onCancel}>{dictionary?.cancel || "Cancel"}</Button>
					{editable
						? <Button primary onClick={onSave}>{dictionary?.save || "Save"}</Button>
						: <Button primary onClick={onEdit}>{dictionary?.edit || "Edit"}</Button>
					}
				</div>
			</div>
			<FARateSetup
				budgetPeriods={setupData.budgetPeriods || DEFAULT_BUDGET_PERIODS}
				editable={editable}
				rateType={setupData?.rateType}
				onChange={onBudgetPeriodsChange}
			/>
			<CopyToFrom
				copyToFrom={setupData?.copyToFrom}
				budgetPeriods={setupData.budgetPeriods}
				editable={editable}
				onChange={onCopyToFromChange}
			/>
			<CurrencyFormatSelector
				editable={editable}
				onChange={onDisplayCentsChange}
				displayCents={setupData.displayCents}
			/>
			<ConfirmOverwriteModal
				open={confirmOverwriteDataOpen}
				onContinue={onConfirmOverwrite}
				onCancel={() => setConfirmOverwriteDataOpen(false)}
			/>
		</>
	);
};

export default SetupMainView;
